import { MsalProvider } from '@azure/msal-react';
import { createRoot } from 'react-dom/client';
import '@/styles/index.css';
import App from '@/App';
import { configureApiClient } from '@/api/client';
import { createAuthenticatedAxios } from '@/lib/createAuthenticatedAxios';
import { initializeMsal, msalInstance } from '@/lib/msal';
import { runtimeConfig } from '@/runtimeConfig';

function renderAuthFailure(message: string) {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    console.error('Root element not found');
    return;
  }
  createRoot(rootElement).render(
    <div className="min-h-screen bg-[#f8fafc] text-[#0f172b] flex items-center justify-center p-6">
      <div className="max-w-[680px] w-full rounded-xl border border-[#e2e8f0] bg-white p-6 shadow-sm">
        <h1 className="text-[22px] font-bold tracking-[-0.02em]">
          Authentication Failed
        </h1>
        <p className="mt-3 text-[14px] leading-6 text-[#475467]">{message}</p>
        <p className="mt-2 text-[13px] leading-5 text-[#667085]">
          Verify runtime configuration (APP_CONFIG or VITE_* values) and retry.
        </p>
      </div>
    </div>,
  );
}

async function bootstrap() {
  try {
    await initializeMsal();
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Unknown MSAL error';
    renderAuthFailure(message);
    return;
  }

  const axiosInstance = createAuthenticatedAxios(
    msalInstance,
    runtimeConfig.ApiBaseUrl.Or('/api'),
    runtimeConfig.MsalScopes.Or(
      `api://${runtimeConfig.MsalClientId.asString()}/.default`,
    )
      .split(/[\s,]+/)
      .map((scope) => scope.trim())
      .filter(Boolean),
  );

  configureApiClient(axiosInstance);

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    console.error('Root element not found');
    return;
  }
  createRoot(rootElement).render(
    <MsalProvider instance={msalInstance}>
      <App />
    </MsalProvider>,
  );
}

void bootstrap();
